import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Home, Ruler, Layers, Building, Check, Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { PillButton } from "@/components/PillButton";
import { scrollToId } from "@/lib/scroll";
import { openWhatsApp } from "@/lib/whatsapp";
import { useScrollLock } from "@/hooks/useScrollLock";

const Fact = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-2.5 rounded-2xl border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.03)] px-3.5 py-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" />
      <div>
        <div className="font-inter text-[10px] uppercase tracking-[0.16em] text-[var(--text-muted)]">{label}</div>
        <div className="mt-0.5 text-sm text-[var(--text-primary)]">{value}</div>
      </div>
    </div>
  ) : null;

export const ProjectModal = ({ project, onClose }) => {
  useScrollLock(!!project);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [project, onClose]);

  const enquireNow = () => {
    onClose();
    setTimeout(() => scrollToId("contact"), 140);
  };

  const d = project?.details || {};

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="fixed inset-0 bg-[rgba(8,8,8,0.72)] backdrop-blur-md" onClick={onClose} aria-hidden="true" />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={project.name}
            data-testid="project-modal"
            data-lenis-prevent
            initial={{ opacity: 0, scale: 0.97, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 my-auto flex max-h-[94dvh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-[var(--border-hover)] bg-[rgba(20,18,16,0.92)] shadow-[var(--shadow-md),var(--shadow-warm)] backdrop-blur-2xl"
          >
            <button
              data-testid="project-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 z-30 grid h-10 w-10 place-items-center rounded-full border border-[var(--border-subtle)] bg-[rgba(12,12,12,0.7)] text-[var(--text-primary)] backdrop-blur transition-colors hover:border-[var(--border-hover)] focus-ring sm:right-4 sm:top-4"
            >
              <X size={18} />
            </button>

            {/* Scrollable inner content */}
            <div
              data-lenis-prevent
              className="flex-1 overflow-y-auto overscroll-contain"
            >
              {/* Hero banner */}
              <div className="relative h-44 w-full shrink-0 overflow-hidden sm:h-56 md:h-64">
                <img src={project.image} alt={project.name} className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(20,18,16,0.98)] via-[rgba(20,18,16,0.35)] to-transparent" />
                <span className="absolute left-4 top-4 rounded-full border border-[var(--border-hover)] bg-[rgba(12,12,12,0.6)] px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-[var(--accent-cream)] backdrop-blur-md sm:left-5 sm:top-5 sm:text-[11px]">
                  {project.badge}
                </span>
                <div className="absolute bottom-4 left-4 right-4 sm:left-5 sm:right-5">
                  <div className="font-inter text-[10px] uppercase tracking-[0.2em] text-[var(--text-accent)] sm:text-[11px]">{project.type}</div>
                  <h3 className="mt-1 font-playfair text-2xl leading-tight text-[var(--text-primary)] sm:text-3xl md:text-4xl">{project.name}</h3>
                  <div className="mt-2 flex items-start gap-2 text-[13px] text-[var(--text-secondary)] sm:text-sm">
                    <MapPin size={15} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" /> {project.location}
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6 md:p-8">
                <p className="text-[15px] leading-relaxed text-[var(--text-secondary)]">{project.description}</p>

                {d.highlight && (
                  <div className="mt-5 rounded-2xl border border-[var(--border-hover)] bg-[rgba(255,245,230,0.05)] px-4 py-3 text-sm text-[var(--accent-cream)]">
                    ✦ {d.highlight}
                  </div>
                )}

                {/* Quick facts */}
                {(d.config || d.area || d.plan || d.developer) && (
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Fact icon={Home} label="Configuration" value={d.config} />
                    <Fact icon={Ruler} label="Area" value={d.area} />
                    <Fact icon={Layers} label="Floor Plan" value={d.plan} />
                    <Fact icon={Building} label="Developed By" value={d.developer} />
                  </div>
                )}

                {/* Site address */}
                {d.siteAddress && (
                  <div className="mt-6">
                    <h4 className="font-inter text-[11px] uppercase tracking-[0.2em] text-[var(--text-accent)]">Site Address</h4>
                    <p className="mt-2 flex items-start gap-2 text-sm leading-relaxed text-[var(--text-secondary)]">
                      <MapPin size={15} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" />
                      {d.siteAddress}
                    </p>
                  </div>
                )}

                {/* Specifications */}
                {Array.isArray(d.specifications) && d.specifications.length > 0 && (
                  <div className="mt-7">
                    <h4 className="font-inter text-[11px] uppercase tracking-[0.2em] text-[var(--text-accent)]">Specifications &amp; Amenities</h4>
                    <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                      {d.specifications.map((s) => (
                        <div key={s.label} className="flex items-start gap-2.5">
                          <Check size={16} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" />
                          <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
                            <span className="font-semibold text-[var(--text-primary)]">{s.label}: </span>
                            {s.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Booking contacts */}
                {Array.isArray(d.booking) && d.booking.length > 0 && (
                  <div className="mt-7">
                    <h4 className="font-inter text-[11px] uppercase tracking-[0.2em] text-[var(--text-accent)]">Booking Contact</h4>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                      {d.booking.map((b) => (
                        <a
                          key={b.tel}
                          href={`tel:${b.tel}`}
                          className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.03)] px-4 py-2 text-sm text-[var(--text-primary)] transition-colors hover:border-[var(--border-hover)] hover:bg-[rgba(255,245,230,0.07)] focus-ring"
                        >
                          <Phone size={14} className="text-[var(--accent-primary)]" />
                          <span>{b.name}</span>
                          <span className="text-[var(--text-muted)]">{b.phone}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky CTA footer — always visible */}
            <div className="flex shrink-0 flex-wrap gap-3 border-t border-[var(--border-subtle)] bg-[rgba(16,14,12,0.9)] p-4 backdrop-blur-xl sm:px-6 md:px-8">
              <PillButton data-testid="project-modal-enquire" onClick={enquireNow}>Enquire Now</PillButton>
              <PillButton
                variant="outline"
                icon={FaWhatsapp}
                onClick={() => openWhatsApp(`Hello, I'm interested in *${project.name}* (${d.config || project.type})${project.location ? ` at ${project.location}` : ""}. Please share more details.`)}
              >
                WhatsApp
              </PillButton>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
